/**
 * Универсальный хук для работы с синхронизируемыми сущностями.
 *
 * useEntityList — получает плоский список всех записей сущности.
 * При первом pull: since=0 (полная выгрузка). При последующих:
 * since=lastServerTimestamp (только изменения), результат мержится с кэшем.
 *
 * useEntitySave — push (create/update/delete) с optimistic UI; затем
 * инкрементальный pull (since = курсор) — сервер вернёт только что
 * записанные версии. Полный pull — только если сервер отклонил часть записей
 * (LWW: на сервере более новая версия, а в кэше — наша оптимистичная).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { syncApi, makeEntity, now } from '../sync'
import { syncEntitySchema } from '../schemas'
import { filterPulled, mergePulled, pullAllPages, rememberServerTimestamp, resetSince, sinceFor } from '../entitySyncState'
import type { EntityType, SyncEntity } from '../../types/entities'

const queryKey = (entity: EntityType) => ['sync', entity] as const

/** Возвращает список всех записей сущности (активных и удалённых).
 *  Удалённые отфильтруйте сами в селекторе если нужно. */
export function useEntityList<T extends SyncEntity>(
  entity: EntityType,
  options: { includeDeleted?: boolean } = {},
) {
  const qc = useQueryClient()
  return useQuery({
    queryKey: queryKey(entity),
    queryFn: async () => {
      const prev = qc.getQueryData<T[]>(queryKey(entity))
      const since = sinceFor(entity, prev !== undefined)
      // Дочитываем все страницы: раньше полный pull обрезался на 5000 записях
      // и кэш заменялся неполным списком.
      const resp = await pullAllPages((s, limit) => syncApi.pull<T>(entity, s, limit), since)

      // Обновляем since для следующего incremental-pull
      rememberServerTimestamp(entity, resp.serverTimestamp)

      // Валидация базовых полей через Zod + формат сущности. Битая или чужая
      // (например, attendance от KMP-клиента без date/mark) запись пропускается,
      // а не роняет весь список.
      const { records: newRecords, skipped } = filterPulled<T>(
        entity,
        resp.records,
        (r): r is T => syncEntitySchema.safeParse(r).success,
      )
      if (skipped > 0) {
        console.warn(`[sync] ${entity}: пропущено ${skipped} записей не в web-формате`)
      }

      // Incremental merge: объединяем новые записи с существующим кэшем.
      // Пустой инкрементальный ответ = «изменений нет»: раньше он затирал
      // кэш пустым списком и данные пропадали с экрана при рефетче.
      return mergePulled(prev, newRecords, since)
    },
    staleTime: 30_000,
    select: options.includeDeleted
      ? undefined
      : (records) => records.filter((r) => !r.deletedAt),
  })
}

/** Push одной или нескольких записей. Используется как для create, так и для update. */
export function useEntitySave<T extends SyncEntity>(entity: EntityType) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (records: T | T[]) => {
      const arr = Array.isArray(records) ? records : [records]
      return syncApi.push(entity, arr)
    },
    onMutate: async (records) => {
      const arr = Array.isArray(records) ? records : [records]
      await qc.cancelQueries({ queryKey: queryKey(entity) })
      const prev = qc.getQueryData<T[]>(queryKey(entity)) ?? []
      const byId = new Map(prev.map((r) => [r.id, r]))
      for (const r of arr) byId.set(r.id, r)
      qc.setQueryData(queryKey(entity), Array.from(byId.values()))
      return { prev }
    },
    onSuccess: (resp) => {
      // Отклонённые по LWW записи: серверная версия могла быть записана раньше
      // текущего курсора и инкрементом не придёт — нужен полный pull.
      if ((resp?.rejected ?? 0) > 0 || (resp?.rejectedIds?.length ?? 0) > 0) resetSince(entity)
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(queryKey(entity), ctx.prev)
    },
    onSettled: () => {
      // Инкрементальный pull от текущего курсора (не полный, как раньше:
      // каждая выставленная оценка скачивала до 5000 записей).
      qc.invalidateQueries({ queryKey: queryKey(entity) })
    },
  })
}

/** Soft-delete сущности (выставление deletedAt). */
export function useEntityDelete<T extends SyncEntity>(entity: EntityType) {
  const save = useEntitySave<T>(entity)
  return {
    ...save,
    mutate: (record: T) => save.mutate({ ...record, deletedAt: now(), updatedAt: now() } as T),
    mutateAsync: (record: T) =>
      save.mutateAsync({ ...record, deletedAt: now(), updatedAt: now() } as T),
  }
}

export { makeEntity, now }
